import { useEffect } from 'react'
import type { MensagemDto } from '@crm/shared'
import { IconeArquivo, IconeBaixar } from './icones'
import { PlayerDeVideo } from './PlayerDeVideo'

/**
 * Foto, vídeo e documento abertos por cima da tela, sem sair do CRM.
 *
 * Antes cada um saía por um caminho: foto abria aqui, vídeo dependia do botão
 * de tela cheia do navegador, e documento — e toda mídia da ficha do contato —
 * era um link para "abrir em nova aba". Na janela em que o CRM roda, as duas
 * coisas falham: a tela cheia é recusada pelo navegador, e a "nova aba" vira
 * navegação na mesma aba. O arquivo tomava o lugar do CRM, sem X nem nada, e
 * o único caminho de volta era o botão de voltar do navegador.
 *
 * Aqui nada depende disso: o visualizador ocupa a janela inteira, fecha pelo
 * X, pelo fundo ou pelo Esc, e "Baixar" salva o arquivo em vez de abri-lo.
 */

export interface MidiaParaVer {
  url: string
  tipo: 'imagem' | 'video' | 'audio' | 'documento'
  mime?: string | null
  nome?: string | null
}

/** O que o visualizador precisa, tirado de uma mensagem da conversa. */
export function midiaDaMensagem(mensagem: MensagemDto): MidiaParaVer | null {
  if (!mensagem.midiaUrl) return null
  const tipo =
    mensagem.tipo === 'imagem' || mensagem.tipo === 'video' || mensagem.tipo === 'audio'
      ? mensagem.tipo
      : 'documento'
  return {
    url: mensagem.midiaUrl,
    tipo,
    mime: mensagem.midiaMime,
    nome: mensagem.midiaNome ?? mensagem.conteudo,
  }
}

function ehPdf(midia: MidiaParaVer): boolean {
  return (
    (midia.mime ?? '').toLowerCase().startsWith('application/pdf') ||
    /\.pdf$/i.test(midia.nome ?? '')
  )
}

export function VisualizadorDeMidia({
  midia,
  aoFechar,
}: {
  midia: MidiaParaVer
  aoFechar: () => void
}) {
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') aoFechar()
    }
    document.addEventListener('keydown', aoTeclar)
    // Trava a rolagem de fundo enquanto está aberto.
    const rolagem = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', aoTeclar)
      document.body.style.overflow = rolagem
    }
  }, [aoFechar])

  const pdf = midia.tipo === 'documento' && ehPdf(midia)

  return (
    <div
      className="fixed inset-0 z-[70] flex flex-col bg-black/85"
      onClick={aoFechar}
      role="dialog"
      aria-modal="true"
      aria-label={midia.nome ?? 'Mídia'}
    >
      <div className="flex shrink-0 items-center gap-3 px-4 py-3 text-white">
        <p className="min-w-0 flex-1 truncate text-sm text-white/80">{midia.nome ?? ''}</p>
        {/*
          "Baixar" no lugar de "abrir em outra aba".

          Abrir em outra aba era mais uma saída do CRM — e é justamente a que,
          nesta janela, vira navegação na mesma aba. O atributo `download` salva
          o arquivo sem trocar de página.
        */}
        {/*
          Azul da marca, e não branco translúcido.

          Estes dois são os únicos botões por cima do visualizador, e o fundo
          atrás deles é a foto ou o vídeo do cliente — que pode ser claro,
          escuro ou os dois na mesma imagem. Uma cor própria não depende do que
          está atrás; o branco a 10% sumia em cima de imagem clara.
        */}
        <a
          href={midia.url}
          download={midia.nome ?? true}
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1.5 rounded-full bg-marca px-4 py-2 text-sm font-semibold text-white shadow-media transition hover:bg-marca-escura"
        >
          <IconeBaixar className="h-4 w-4" />
          Baixar
        </a>
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar"
          title="Fechar (Esc)"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-marca text-2xl leading-none text-white shadow-media transition hover:bg-marca-escura"
        >
          ×
        </button>
      </div>

      {/*
        Clique no próprio conteúdo não fecha: dá para mexer no vídeo, rolar o
        PDF e olhar a foto de perto. Só o fundo em volta fecha.
      */}
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 pb-4">
        {midia.tipo === 'imagem' ? (
          <img
            src={midia.url}
            alt={midia.nome ?? ''}
            onClick={(e) => e.stopPropagation()}
            className="max-h-full max-w-full rounded object-contain"
          />
        ) : midia.tipo === 'video' ? (
          /*
            O mesmo player da conversa, e não os controles do navegador.

            Os do navegador não pausam no clique dentro da tela nesta janela, e
            traziam de volta o botão de tela cheia que aqui não tem para onde
            ir: o visualizador já é a janela inteira.
          */
          /*
            Abre parado. Ampliar é para ver melhor, não para começar de novo —
            e vídeo que dispara sozinho numa sala com gente do lado é o tipo de
            coisa que faz procurar o botão de pausa com pressa.
          */
          <PlayerDeVideo
            url={midia.url}
            semTelaCheia
            className="h-full max-h-full w-full max-w-4xl"
          />
        ) : midia.tipo === 'audio' ? (
          <audio
            src={midia.url}
            controls
            autoPlay
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg"
          />
        ) : pdf ? (
          <iframe
            src={midia.url}
            title={midia.nome ?? 'Documento'}
            onClick={(e) => e.stopPropagation()}
            className="h-full w-full max-w-5xl rounded bg-superficie"
          />
        ) : (
          /*
            Planilha, .docx, .zip: o navegador não mostra nenhum deles por
            dentro. Em vez de uma tela em branco, o nome do arquivo e o botão
            que resolve.
          */
          <div
            onClick={(e) => e.stopPropagation()}
            className="flex flex-col items-center gap-3 rounded-lg bg-superficie px-8 py-6 text-center"
          >
            <IconeArquivo className="h-10 w-10 text-tinta-400" />
            <p className="max-w-xs break-words text-sm font-medium text-tinta-900">
              {midia.nome ?? 'Documento'}
            </p>
            <p className="text-xs text-tinta-500">Este tipo de arquivo não abre aqui dentro.</p>
            <a
              href={midia.url}
              download={midia.nome ?? true}
              className="inline-flex items-center gap-1.5 rounded-full bg-marca px-4 py-2 text-sm font-semibold text-white transition hover:bg-marca-escura"
            >
              <IconeBaixar className="h-4 w-4" />
              Baixar
            </a>
          </div>
        )}
      </div>
    </div>
  )
}
